import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Flame } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Marquee } from "../components/site/Marquee";
import { Countdown } from "../components/site/Countdown";
import { Seo } from "../components/site/Seo";
import { DPS_MEDIA, STATIONS, MERCH, MARQUEE_ITEMS } from "../lib/content";
import { trackEvent } from "../lib/analytics";

const STATS = [
  ["8KM", "Total Running"],
  ["8", "Workout Stations"],
  ["90MIN", "Training Sessions"],
  ["$20", "Drop-In Rate"],
];

const CHAPTERS = [
  {
    n: "01",
    title: "The Format",
    body: "HYROX is the World Series of Fitness Racing: 8 × 1km runs, each followed by a functional workout station. Same course, same weights, every race on the planet. We train the exact standard — nothing watered down.",
    link: ["/stations", "Break down the 8 stations"],
  },
  {
    n: "02",
    title: "The Simulation",
    body: "Full race-day dress rehearsals, Hyrox Ninja style. Wave starts, judged reps, real transitions, the roxzone burn. You'll know your race pace before you ever step into the arena.",
    link: ["/simulation", "See simulation days"],
  },
  {
    n: "03",
    title: "The Mindset",
    body: "NinjAthlete isn't a logo — it's the athlete who shows up hybrid: strong enough to move the sled, engined enough to run it back. Born in Dallas. Built at DPS. Forged in the 214.",
    link: ["/merch", "Rep the co-brand"],
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div data-testid="home-page">
      <Seo
        title="HYROX Training in Dallas | HyroxNinja"
        description="The home of Hyrox Ninja — HYROX training in Dallas with Dallas Performance Systems: specialty sessions, full race simulations, the 8-station format, training videos and the NinjAthlete merch drop."
      />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden grain">
        <motion.div className="absolute inset-0" style={{ y: videoY, scale: videoScale }}>
          <video
            className="w-full h-full object-cover"
            src={DPS_MEDIA.heroVideo}
            poster={DPS_MEDIA.heroPoster}
            preload="metadata"
            autoPlay
            muted
            loop
            playsInline
            aria-label="Athletes training at Dallas Performance Systems"
            data-testid="hero-video"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/55 to-[#070707]/30" />
        </motion.div>

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full pt-36 pb-10">
          <motion.p
            className="eyebrow mb-5 flex items-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <span className="inline-block w-8 h-px bg-volt" />
            Dallas Performance Systems × NinjAthlete — hyroxninja.com
          </motion.p>

          <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-8">
            <MaskedLine delay={0.35}>8 Runs.</MaskedLine>
            <MaskedLine delay={0.5}>8 Stations.</MaskedLine>
            <MaskedLine delay={0.65} className="text-volt">
              One Standard.
            </MaskedLine>
          </h1>

          <motion.p
            className="text-zinc-300 max-w-xl text-base leading-relaxed mb-8"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
          >
            The dedicated home of HYROX training in Dallas. Specialty stage
            training, full race simulations, and the NinjAthlete
            drop — everything you need to get to the start line ready.
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-4 mb-14"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
          >
            <Link to="/events" className="btn-volt" data-testid="hero-book-button" onClick={() => trackEvent("booking_click", { placement: "home_hero", service: "general" })}>
              Book a Session <ArrowRight size={17} />
            </Link>
            <Link to="/simulation" className="btn-ghost" data-testid="hero-simulation-button">
              <Flame size={16} /> Full Simulation
            </Link>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 border border-white/10 divide-x divide-white/10 bg-[#070707]/70 backdrop-blur"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            data-testid="hero-stats-bar"
          >
            {STATS.map(([v, l]) => (
              <div key={l} className="p-4 lg:p-5">
                <p className="font-head font-black text-2xl lg:text-3xl text-volt leading-none">{v}</p>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* MANIFESTO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32">
        <Reveal>
          <p className="eyebrow mb-3">The Manifesto</p>
          <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-16">
            Why we train like this
          </h2>
        </Reveal>
        <div className="flex flex-col">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="grid md:grid-cols-12 gap-6 py-12 border-t border-white/10 items-start" data-testid={`manifesto-chapter-${c.n}`}>
                <span className="chapter-num md:col-span-3">{c.n}</span>
                <div className="md:col-span-4">
                  <h3 className="font-head font-extrabold uppercase text-2xl sm:text-3xl tracking-tight">{c.title}</h3>
                </div>
                <div className="md:col-span-5">
                  <p className="text-zinc-400 leading-relaxed mb-4">{c.body}</p>
                  <Link to={c.link[0]} className="inline-flex items-center gap-2 text-volt font-mono2 text-xs uppercase tracking-[0.2em] hover:gap-3 transition-all" data-testid={`manifesto-link-${c.n}`}>
                    {c.link[1]} <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STATIONS STRIP */}
      <section className="bg-[#0c0c0c] border-y border-white/10 py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <p className="eyebrow mb-3">The Course</p>
              <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl">
                Run <span className="text-outline-volt">1km</span> — then hit the station
              </h2>
            </div>
            <Link to="/stations" className="btn-ghost" data-testid="home-stations-link">
              Full Breakdown <ArrowRight size={16} />
            </Link>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4">
            {STATIONS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.05}>
                <Link to="/stations" className="station-row block p-6 h-full border-x-0 sm:border-r border-white/10 group" data-testid={`home-station-${s.n}`}>
                  <p className="font-head font-black text-4xl text-outline group-hover:text-volt group-hover:[-webkit-text-stroke:0] transition-all">{s.n}</p>
                  <p className="font-head font-bold uppercase text-xl mt-3">{s.name}</p>
                  <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{s.metric}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SIMULATION CALLOUT */}
      <section className="relative overflow-hidden">
        <img src={DPS_MEDIA.bg.home} alt="Athletes training at Dallas Performance Systems" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070707] via-[#070707]/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-28 lg:py-36">
          <Reveal>
            <p className="eyebrow mb-4">Race-Day Rehearsal</p>
            <h2 className="font-head font-black uppercase tracking-tighter leading-none text-4xl sm:text-5xl lg:text-6xl mb-6">
              Full HYROX<br />
              <span className="text-volt">Simulation</span>
            </h2>
            <p className="font-head uppercase text-xl sm:text-2xl text-zinc-300 mb-2 tracking-wide">Sept 13 · Oct 25</p>
            <p className="text-zinc-400 max-w-lg leading-relaxed mb-8">
              The complete race — all 8 runs, all 8 stations, wave starts and
              judged standards — right here in Dallas. Find your pace, test your
              gear, and know your number before race weekend.
            </p>
            <Countdown />
            <div className="flex flex-wrap gap-4">
              <Link to="/events" className="btn-volt" data-testid="home-sim-book-button" onClick={() => trackEvent("booking_click", { placement: "home_simulation", service: "simulation" })}>
                Book the Simulation <ArrowRight size={17} />
              </Link>
              <Link to="/simulation" className="btn-ghost" data-testid="home-sim-details-link">
                Simulation Details
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VIDEO + MERCH TEASERS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-2 gap-8">
        <Reveal>
          <Link to="/videos" className="group block relative overflow-hidden border border-white/10 h-full min-h-[320px]" data-testid="home-videos-teaser">
            <video className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity" src={DPS_MEDIA.teaser} preload="metadata" muted loop playsInline autoPlay aria-label="DPS training footage preview" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070707] to-transparent" />
            <div className="relative p-8 flex flex-col justify-end h-full min-h-[320px]">
              <span className="w-14 h-14 rounded-full bg-volt text-black flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Play size={22} fill="currentColor" />
              </span>
              <p className="eyebrow mb-2">Hype & Video Vault</p>
              <h3 className="font-head font-extrabold uppercase text-2xl sm:text-3xl tracking-tight">Step inside the training vault</h3>
            </div>
          </Link>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/merch" className="group block relative overflow-hidden border border-white/10 h-full min-h-[320px] bg-[#0c0c0c]" data-testid="home-merch-teaser">
            <div className="absolute inset-0 grid grid-cols-3 opacity-70 group-hover:opacity-90 transition-opacity">
              {MERCH.slice(0, 3).map((m) => (
                <img key={m.id} src={m.img} alt={m.name} className="w-full h-full object-cover" />
              ))}
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/40 to-transparent" />
            <div className="relative p-8 flex flex-col justify-end h-full min-h-[320px]">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] bg-white text-black px-3 py-1 w-fit mb-4">Coming Soon</span>
              <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-zinc-300 mb-2">Hyrox Ninja × NinjAthlete</p>
              <h3 className="font-head font-extrabold uppercase text-2xl sm:text-3xl tracking-tight">The black & white 214 drop</h3>
            </div>
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
